import {
  aabbFromPoints,
  aabbSize,
  edgeLengths,
  EPS,
  perimeter,
  polygonArea,
  rectanglePolygon,
  type Vec2,
} from '@indusketch/geometry';
import {
  overlapArea,
  polygonContainsPolygon,
  regionArea,
  unionPolygons,
  type Region,
} from '@indusketch/geometry';
import type { ProjectDocument, Site } from './types.js';
import { defaultName } from './texts.js';

/** Parcels in a stable order (by name, then id) for lists, reports and numbering. */
export function sitesOf(doc: Pick<ProjectDocument, 'sites'>): Site[] {
  return Object.values(doc.sites).sort(
    (a, b) => a.name.localeCompare(b.name, 'de', { numeric: true }) || a.id.localeCompare(b.id),
  );
}

/** Overall site: the union of all parcels (adjacent parcels merge into one region). */
export function siteRegions(doc: Pick<ProjectDocument, 'sites'>): Region[] {
  return unionPolygons(Object.values(doc.sites).map((s) => s.boundary));
}

export function totalSiteArea(doc: Pick<ProjectDocument, 'sites'>): number | null {
  if (Object.keys(doc.sites).length === 0) return null;
  return siteRegions(doc).reduce((s, r) => s + regionArea(r), 0);
}

/** Overlapping parcels would count twice; a warning points the user to it. */
export const siteOverlapArea = (doc: Pick<ProjectDocument, 'sites'>): number =>
  overlapArea(Object.values(doc.sites).map((s) => s.boundary));

/** Whether a footprint lies completely on the overall site (it may span several parcels). */
export function onSite(regions: readonly Region[], footprint: readonly Vec2[]): boolean {
  return regions.some(
    (r) =>
      polygonContainsPolygon(r.outer, footprint) &&
      r.holes.every((h) => !footprint.some((p) => polygonContainsPolygon(h, [p]))),
  );
}

/** Bounding box over all parcels, e.g. to place new halls in the middle of the site. */
export function sitesBounds(doc: Pick<ProjectDocument, 'sites'>) {
  return aabbFromPoints(Object.values(doc.sites).flatMap((s) => s.boundary));
}

/** Next free default name, e.g. "Grundstück 2". */
export function nextSiteName(doc: Pick<ProjectDocument, 'sites'>): string {
  const n = Object.keys(doc.sites).length;
  return n === 0 ? defaultName('site') : defaultName('siteNumbered', { n: n + 1 });
}

export function rectangleSite(
  id: string,
  width: number,
  length: number,
  name = defaultName('site'),
): Site {
  return { id, name, boundary: rectanglePolygon(width, length), locked: false };
}

/**
 * L-shaped site: a width × length rectangle whose north-east corner is cut out by
 * cutWidth × cutLength.
 */
export function lShapeSite(
  id: string,
  width: number,
  length: number,
  cutWidth: number,
  cutLength: number,
  name = defaultName('site'),
): Site {
  return {
    id,
    name,
    boundary: [
      { x: 0, y: 0 },
      { x: width, y: 0 },
      { x: width, y: length - cutLength },
      { x: width - cutWidth, y: length - cutLength },
      { x: width - cutWidth, y: length },
      { x: 0, y: length },
    ],
    locked: false,
  };
}

/**
 * Trapezoid site: `bottom` wide along the south edge, `top` wide along the north edge, `length`
 * apart. `shift` moves the north edge east relative to the south edge's west corner.
 */
export function trapezoidSite(
  id: string,
  bottom: number,
  top: number,
  length: number,
  shift = (bottom - top) / 2,
  name = defaultName('site'),
): Site {
  return {
    id,
    name,
    boundary: [
      { x: 0, y: 0 },
      { x: bottom, y: 0 },
      { x: shift + top, y: length },
      { x: shift, y: length },
    ],
    locked: false,
  };
}

export interface SiteMetrics {
  readonly area: number;
  readonly perimeter: number;
  readonly edgeLengths: readonly number[];
  /** Overall extent of the axis-aligned bounding box. */
  readonly extent: Vec2;
}

export function siteMetrics(site: Site): SiteMetrics {
  const box = aabbFromPoints(site.boundary);
  return {
    area: polygonArea(site.boundary),
    perimeter: perimeter(site.boundary),
    edgeLengths: edgeLengths(site.boundary),
    extent: box ? aabbSize(box) : { x: 0, y: 0 },
  };
}

/** Returns width/length if the boundary is an axis-aligned rectangle, else null. */
export function axisAlignedRectangle(
  boundary: readonly Vec2[],
): { origin: Vec2; width: number; length: number } | null {
  if (boundary.length !== 4) return null;
  const box = aabbFromPoints(boundary);
  if (!box) return null;
  const onCorner = boundary.every(
    (p) =>
      (Math.abs(p.x - box.min.x) < EPS || Math.abs(p.x - box.max.x) < EPS) &&
      (Math.abs(p.y - box.min.y) < EPS || Math.abs(p.y - box.max.y) < EPS),
  );
  const distinct = new Set(
    boundary.map((p) => `${Math.round(p.x * 1e6)}:${Math.round(p.y * 1e6)}`),
  );
  if (!onCorner || distinct.size !== 4) return null;
  const size = aabbSize(box);
  return { origin: box.min, width: size.x, length: size.y };
}
